import React from "react";
import { useNavigate } from "react-router-dom";
import BlogCover from "./BlogCover";
import blogPlaceholder from "../images/blog_placeholder.png";

// ---- Content helpers (shared shape with the home blog preview) ----
const getOrdinalSuffix = (n) => {
  if (n > 3 && n < 21) return "th";
  switch (n % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const formatBlogDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  const day = date.getDate();
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return `Posted ${day}${getOrdinalSuffix(day)} ${months[date.getMonth()]}, ${date.getFullYear()}`;
};

const extractImageFromMarkdown = (text) => {
  if (!text) return null;
  const m = text.match(/!\[([^\]]*)\]\(([^)]+)\)/);
  return m ? m[2] : null;
};

const stripMarkdown = (text) => {
  if (!text) return "";
  return text
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/https?:\/\/[^\s]+/g, "")
    .replace(/[#*<>[\]()!\n]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const createSlug = (title) => {
  if (!title) return "";
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const estimateReadTime = (content) => {
  if (!content) return "3 min read";
  const words = stripMarkdown(content).split(/\s+/).length;
  const mins = Math.max(1, Math.round(words / 200));
  return `${mins} min read`;
};

const BlogCard = ({ blog }) => {
  const navigate = useNavigate();

  const coverImage =
    blog.header_image_url ||
    extractImageFromMarkdown(blog.blog_content) ||
    blogPlaceholder;

  const meta = [formatBlogDate(blog.created_at), estimateReadTime(blog.blog_content)]
    .filter(Boolean)
    .join(" · ");

  const excerpt = stripMarkdown(blog.blog_content).slice(0, 130);

  return (
    <div
      className="blog-grid-card"
      onClick={() => navigate(`/blog/${blog.id}/${createSlug(blog.blog_title)}`)}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        cursor: "pointer",
        height: "100%",
      }}
    >
      <BlogCover
        src={coverImage}
        alt={blog.blog_title}
        onError={(e) => {
          e.target.src = blogPlaceholder;
        }}
        imgClassName="blog-grid-card-img"
        style={{
          width: "100%",
          height: "clamp(180px, 17vw, 240px)",
          borderRadius: "16px",
        }}
        imgStyle={{ transition: "transform 0.4s ease" }}
      />

      <p
        style={{
          fontFamily: "'Geist', sans-serif",
          fontWeight: 400,
          fontSize: "clamp(11px, 0.9vw, 13px)",
          lineHeight: 1.6,
          color: "#5E5E5E",
          margin: 0,
        }}
      >
        {meta}
      </p>

      <p
        style={{
          fontFamily: "'Geist', sans-serif",
          fontWeight: 500,
          fontSize: "clamp(15px, 1.18vw, 18px)",
          lineHeight: 1.4,
          color: "#132A22",
          margin: 0,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {blog.blog_title}
      </p>

      <p
        style={{
          fontFamily: "'Geist', sans-serif",
          fontWeight: 400,
          fontSize: "clamp(13px, 0.97vw, 14px)",
          lineHeight: 1.7,
          color: "#444B47",
          margin: 0,
        }}
      >
        <span
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {excerpt}
          {excerpt.length >= 130 ? "…" : ""}
        </span>
        <span
          className="blog-read-more"
          style={{
            color: "#3F5445",
            textDecoration: "underline",
            fontWeight: 500,
          }}
        >
          Read more
        </span>
      </p>
    </div>
  );
};

export default BlogCard;
